/**
 * /admin — onglet « Demandes d'achat » (2026-10-12).
 *
 * Le pendant de « Véhicules à vendre » : même logique, même grammaire d'écran.
 * Le propriétaire fait la recherche À LA MAIN et valide CHAQUE proposition ;
 * rien ne part à l'acheteur tout seul.
 *
 * Les coordonnées de l'acheteur ne vivent QUE dans cet écran : l'alerte
 * Telegram ne les porte pas (cf. services/recherche.message_alerte).
 *
 * LES CORRESPONDANCES SONT CHARGÉES À LA DEMANDE, une demande à la fois : les
 * calculer pour 200 demandes d'un coup ferait 200 balayages du vivier sur une
 * page qu'on ouvre pour lire une liste. C'est le même moteur que l'aperçu de
 * l'acheteur, mais ici avec le lien et la source — visibles de l'admin seul.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { lieu, nombre, nomVoiture } from '../components/Affaires/format';
import api from '../services/api';
import AdminHistorique from './AdminHistorique';
import './AdminRecherche.css';

const LIBELLES = {
  nouvelle: 'Nouvelle', en_recherche: 'En recherche', proposee: 'Proposée',
  conclue: 'Conclue', abandonnee: 'Abandonnée',
};
// Les libellés des boutons, par statut d'arrivée. Les transitions permises
// viennent du SERVEUR (`suites`) : la règle vit dans services/recherche.py,
// cet écran ne fait que l'habiller.
const ACTIONS = {
  en_recherche: ['Prendre en recherche', 'bleu'],
  proposee: ['Marquer proposée', 'bleu'],
  conclue: ['Marquer conclue', 'vert'],
  abandonnee: ['Abandonner', 'rouge'],
};
// Un passage qui exige une note, côté serveur comme ici : on ne note pas
// « proposée » sans dire ce qu'on a proposé.
const NOTE_OBLIGATOIRE = new Set(['proposee', 'abandonnee']);
const CONFIANCE = { SOLIDE: 'cote solide', CORRECT: 'cote correcte' };

const euros = (n) => (n == null ? '—' : `${nombre(n)} €`);
const quand = (iso) => (iso
  ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
  : '—');

function Vehicule({ a }) {
  return (
    <li className="zk-adr-annonce">
      <div>
        <b>{nomVoiture(a)}</b>
        <span className="zk-adr-specs">
          {[a.annee, a.km != null ? `${nombre(a.km)} km` : null, a.energie, a.boite, lieu(a)]
            .filter(Boolean).join(' · ')}
        </span>
      </div>
      <div className="zk-adr-chiffres-annonce">
        <b>{euros(a.prix)}</b>
        {a.ecart_eur != null && a.ecart_eur > 0 && (
          <span className="zk-adr-ecart">−{nombre(a.ecart_eur)} € ({a.ecart_pct} %)</span>
        )}
        <small>{CONFIANCE[a.fiabilite] || 'cote mesurée'}</small>
      </div>
      {a.lien && (
        <a href={a.lien} target="_blank" rel="noreferrer" className="zk-adm-btn">
          Ouvrir chez {a.source || 'la source'}
        </a>
      )}
    </li>
  );
}

function Demande({ demande, onStatut, onNote, onCharger, occupe }) {
  const [note, setNote] = useState('');
  const [cible, setCible] = useState(null);
  const c = demande.criteres;
  const quoi = c.marques.length
    ? c.marques.map((m, i) => [m, c.modeles[i]].filter(Boolean).join(' ')).join(' · ')
    : 'Toutes marques';
  const limites = [
    c.annee_min && `dès ${c.annee_min}`,
    c.km_max && `≤ ${nombre(c.km_max)} km`,
    c.energie, c.boite,
  ].filter(Boolean);
  const ou = c.departement
    ? `${c.departement_nom} (${c.departement})${c.rayon_km ? ` + ${c.rayon_km} km` : ''}`
    : 'Toute la France';

  async function valider(e) {
    e.preventDefault();
    const action = cible;
    setCible(null);
    await onStatut(demande.id, action, note.trim() || null);
    setNote('');
  }

  return (
    <article className="zk-carte zk-adr-demande">
      <header>
        <h3>{quoi}</h3>
        <span className={`zk-adr-statut ${demande.statut}`}>
          {LIBELLES[demande.statut] || demande.statut}
        </span>
      </header>

      <dl className="zk-adr-chiffres">
        <div><dt>Budget</dt><dd>{euros(c.budget_max)}</dd></div>
        <div><dt>Secteur</dt><dd>{ou}</dd></div>
        {/* Le type remplace l'urgence (formulaire minimal du 2026-10-15) :
            il FILTRE les correspondances, l'urgence n'est plus demandée. */}
        <div><dt>Type</dt><dd>{c.type_libelle || '—'}</dd></div>
        <div>
          <dt>Au dépôt</dt>
          <dd>
            {demande.correspondances_au_depot ?? '—'}
            <small>affaires correspondantes</small>
          </dd>
        </div>
      </dl>

      <p className="zk-adr-criteres">
        {limites.length ? limites.join(' · ') : 'Aucune autre limite'}
        {/* L'usage ne filtre PAS le vivier (aucune annonce ne porte d'usage) :
            il est là pour guider la recherche manuelle. */}
        {c.usage_libelle && <em> · usage : {c.usage_libelle} (non filtré, à votre main)</em>}
        {/* Demandes déposées avant le 2026-10-15 : l'urgence reste lisible. */}
        {c.urgence_libelle && <em> · urgence : {c.urgence_libelle}</em>}
      </p>
      {(c.marques_saisies?.length > 0
        && c.marques_saisies.join() !== c.marques.join()) && (
        <p className="zk-adr-saisie">saisi « {c.marques_saisies.join(', ')} »</p>
      )}

      <p className="zk-adr-contact">
        <b>{demande.prenom}</b> · {demande.code_postal} ·{' '}
        <a href={`tel:${demande.telephone}`}>{demande.telephone}</a> ·{' '}
        <a href={`mailto:${demande.email}`}>{demande.email}</a>
      </p>
      <p className="zk-adr-date">
        Déposée le {quand(demande.created_at)}
        {demande.statut_at ? ` · ${LIBELLES[demande.statut]} le ${quand(demande.statut_at)}` : ''}
        {demande.consentement_partenaires
          ? ' · transmission partenaires ACCEPTÉE'
          : ' · transmission partenaires refusée'}
      </p>

      {demande.note && <pre className="zk-adr-note">{demande.note}</pre>}
      <AdminHistorique dossier={demande} libelles={LIBELLES} depot="Déposée" />

      {/* Correspondances actuelles : même moteur que l'aperçu de l'acheteur,
          triées par écart à la cote, avec le lien et la source. */}
      {demande.correspondances === undefined ? (
        <button type="button" className="zk-adm-btn bleu" disabled={occupe}
          onClick={() => onCharger(demande.id)}>
          Voir les annonces qui correspondent aujourd’hui
        </button>
      ) : demande.correspondances.length === 0 ? (
        <p className="zk-adr-vide">Aucune annonce ne correspond en ce moment.</p>
      ) : (
        <div className="zk-adr-correspondances">
          <b>
            {demande.correspondances_total} annonce
            {demande.correspondances_total > 1 ? 's' : ''} correspondent aujourd’hui
            {demande.correspondances_total > demande.correspondances.length
              && ` — les ${demande.correspondances.length} meilleures ci-dessous`}
          </b>
          <ul>{demande.correspondances.map((a) => <Vehicule key={a.id} a={a} />)}</ul>
        </div>
      )}

      {demande.suites?.length > 0 && !cible && (
        <div className="zk-adr-actions">
          {demande.suites.map((statut) => {
            const [libelle, couleur] = ACTIONS[statut] || [statut, ''];
            return (
              <button
                key={statut} type="button" disabled={occupe}
                className={`zk-adm-btn ${couleur}`}
                onClick={() => (NOTE_OBLIGATOIRE.has(statut)
                  ? setCible(statut)
                  : onStatut(demande.id, statut, null))}
              >
                {libelle}
              </button>
            );
          })}
          <button type="button" className="zk-adm-btn" disabled={occupe}
            onClick={() => setCible('note')}>
            Noter un échange
          </button>
        </div>
      )}

      {cible && (
        <form className="zk-adr-formulaire" onSubmit={cible === 'note'
          ? (e) => { e.preventDefault(); onNote(demande.id, note.trim()).then(() => { setCible(null); setNote(''); }); }
          : valider}>
          <label htmlFor={`note-${demande.id}`}>
            {cible === 'proposee' ? 'Qu’avez-vous proposé ?'
              : cible === 'abandonnee' ? 'Pourquoi abandonner ?'
                : 'Que s’est-il passé ?'}
          </label>
          <input
            id={`note-${demande.id}`} required maxLength={1000} value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={cible === 'proposee'
              ? 'Une 208 de 2018, 90 000 km, 9 000 € — envoyée par SMS'
              : cible === 'abandonnee' ? 'Injoignable après trois appels'
                : 'Appelée, elle veut finalement un break'}
          />
          <div className="zk-adr-actions">
            <button type="submit" className="zk-adm-btn bleu" disabled={occupe || !note.trim()}>
              Enregistrer
            </button>
            <button type="button" className="zk-adm-btn"
              onClick={() => { setCible(null); setNote(''); }}>
              Annuler
            </button>
          </div>
        </form>
      )}
    </article>
  );
}

export default function AdminRecherche({ signaler, onChange }) {
  const [demandes, setDemandes] = useState([]);
  const [comptes, setComptes] = useState({});
  const [filtre, setFiltre] = useState('');
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get('/api/admin/recherche', {
        params: filtre ? { statut: filtre } : {},
      });
      setDemandes(data?.demandes || []);
      setComptes(data?.comptes || {});
    } catch {
      signaler?.('Erreur de chargement des demandes d’achat', false);
    }
    setChargement(false);
  }, [filtre, signaler]);

  useEffect(() => { charger(); }, [charger]);

  const remplacer = (id, data) => setDemandes((ds) => ds.map((d) => (d.id === id ? data : d)));

  async function chargerCorrespondances(id) {
    setOccupe(true);
    try {
      const { data } = await api.get(`/api/admin/recherche/${id}/correspondances`);
      remplacer(id, data);
    } catch {
      signaler?.('Correspondances indisponibles', false);
    } finally {
      setOccupe(false);
    }
  }

  async function changerStatut(id, statut, note) {
    setOccupe(true);
    try {
      const { data } = await api.post(`/api/admin/recherche/${id}/statut`, { statut, note });
      remplacer(id, data);
      signaler?.(`Demande #${id} : ${LIBELLES[statut]}`);
      if (filtre) charger();
      onChange?.();
    } catch (err) {
      signaler?.(err.response?.data?.detail?.message || 'Changement de statut refusé', false);
    } finally {
      setOccupe(false);
    }
  }

  async function ajouterNote(id, note) {
    setOccupe(true);
    try {
      const { data } = await api.post(`/api/admin/recherche/${id}/note`, { statut: '', note });
      remplacer(id, data);
      signaler?.(`Demande #${id} : échange noté`);
    } catch (err) {
      signaler?.(err.response?.data?.detail?.message || 'Note refusée', false);
    } finally {
      setOccupe(false);
    }
  }

  const total = Object.values(comptes).reduce((a, b) => a + b, 0);

  return (
    <div>
      <div className="zk-adr-tete">
        <div className="zk-adm-onglets" role="group" aria-label="Filtrer par statut">
          <button type="button" className={`zk-aff-pilule${filtre === '' ? ' on' : ''}`}
            onClick={() => setFiltre('')}>
            Toutes · {total}
          </button>
          {Object.keys(LIBELLES).map((s) => (
            <button key={s} type="button" className={`zk-aff-pilule${filtre === s ? ' on' : ''}`}
              onClick={() => setFiltre(s)}>
              {LIBELLES[s]} · {comptes[s] || 0}
            </button>
          ))}
        </div>
        <a className="zk-adm-btn" href={`/api/admin/recherche/export.csv${filtre ? `?statut=${filtre}` : ''}`}>
          Exporter en CSV
        </a>
      </div>

      {chargement ? (
        <p className="zk-adm-vide">Chargement…</p>
      ) : demandes.length === 0 ? (
        <p className="zk-adm-vide">
          Aucune demande d’achat {filtre ? `au statut « ${LIBELLES[filtre]} »` : 'pour l’instant'}.
        </p>
      ) : (
        <div className="zk-adr-liste">
          {demandes.map((d) => (
            <Demande
              key={d.id} demande={d} onStatut={changerStatut} onNote={ajouterNote}
              onCharger={chargerCorrespondances} occupe={occupe}
            />
          ))}
        </div>
      )}
    </div>
  );
}
